import { useNavigate, useParams } from 'react-router-dom'
import { ShoppingCart, Plus, Minus, Trash2, ArrowLeft, Store as StoreIcon, ArrowRight, Clock } from 'lucide-react'
import { useCart } from '../contexts/CartContext'
import { useAuth } from '../contexts/AuthContext'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import ProductImage from '../components/ProductImage'
import StoreImage from '../components/StoreImage'
import { Store } from '../types'
import { calculateStoreIsOpen, getNextOpeningTime } from '../lib/storeUtils'
import { useStoreDetail } from '../contexts/StoreDetailContext'

const Cart = () => {
  const navigate = useNavigate()
  const { storeId } = useParams<{ storeId?: string }>()
  const { storeCarts, updateQuantity, removeItem, clearCart, clearStoreCart, getStoreCart, isEmpty } = useCart()
  const { isAuthenticated } = useAuth()
  const { getStore, getStoreSync } = useStoreDetail() // Usar StoreDetailContext en lugar de query directa
  const [storeInfo, setStoreInfo] = useState<Store | null>(null)
  const [loadingStore, setLoadingStore] = useState(false)

  // Si estamos en un negocio específico, obtener solo ese carrito
  const currentStoreCart = storeId ? getStoreCart(storeId) : null
  const isInStore = !!storeId // Si hay storeId en la URL, estamos en un negocio

  // Cargar información del store si estamos en un negocio pero no tenemos el carrito
  useEffect(() => {
    if (storeId && !currentStoreCart && !loadingStore) {
      loadStoreInfo()
    } else if (currentStoreCart) {
      // Si tenemos el carrito, usar la info del store del carrito
      setStoreInfo(currentStoreCart.store)
    }
  }, [storeId, currentStoreCart])

  const loadStoreInfo = async () => {
    if (!storeId) return
    
    // Intentar cargar desde cache sincrónicamente primero (más rápido)
    const cachedStore = getStoreSync(storeId)
    if (cachedStore) {
      setStoreInfo(cachedStore)
      setLoadingStore(false)
      // Si tenemos cache válido, no hacer query HTTP (evitar 304 innecesario)
      return
    }
    
    try {
      setLoadingStore(true)
      // Usar StoreDetailContext para cargar el store (elimina query duplicada)
      const loadedStore = await getStore(storeId)
      if (loadedStore) {
        setStoreInfo(loadedStore)
      }
    } catch (error: any) {
      console.error('Error loading store:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedStore) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error('Error al cargar información del negocio')
    } finally {
      setLoadingStore(false)
    }
  }

  // Redirect to login if not authenticated when trying to checkout
  useEffect(() => {
    if (!isAuthenticated && !isEmpty) {
      // Allow viewing cart but will require login at checkout
    }
  }, [isAuthenticated, isEmpty])

  const handleQuantityChange = (storeId: string, itemId: string, newQuantity: number) => {
    if (newQuantity < 1) {
      removeItem(storeId, itemId)
    } else {
      updateQuantity(storeId, itemId, newQuantity)
    }
  }

  const handleCheckout = (storeId: string) => {
    if (!isAuthenticated) {
      toast.error('Debes iniciar sesión para realizar una compra')
      navigate('/login', { state: { from: `/checkout/${storeId}` } })
      return
    }

    const storeCart = storeCarts[storeId]
    if (!storeCart) {
      toast.error('No hay productos en el carrito de este negocio')
      return
    }

    const store = storeCart.store
    const storeTotal = storeCart.items.reduce((sum, item) => sum + (Number(item.total) || 0), 0)
    const minOrderAmount = store.minOrderAmount || 0

    if (storeTotal < minOrderAmount) {
      toast.error(`El pedido mínimo es de RD$${minOrderAmount.toFixed(2)}`)
      return
    }

    navigate(`/checkout/${storeId}`)
  }

  const handleNavigateToStoreCart = (storeId: string) => {
    navigate(`/stores/${storeId}/cart`)
  }

  // Si estamos dentro de un negocio, mostrar solo ese carrito
  if (isInStore) {
    // Si tenemos el carrito, usar su información, sino usar la info del store cargada
    const store = currentStoreCart?.store || storeInfo
    const items = currentStoreCart?.items || []
    
    // Si no tenemos información del store, mostrar loading
    if (!store && loadingStore) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center pb-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 font-medium">Cargando información del negocio...</p>
          </div>
        </div>
      )
    }
    
    // Si no tenemos información del store después de cargar, mostrar error
    if (!store && !loadingStore) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4 pb-20">
          <div className="text-center max-w-md w-full">
            <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-12">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center">
                <StoreIcon className="h-12 w-12 text-red-600" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Negocio no encontrado</h2>
              <p className="text-gray-600 mb-8 text-sm sm:text-base">El negocio que buscas no existe o no está disponible</p>
              <button
                onClick={() => navigate('/stores')}
                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-2xl hover:from-primary-700 hover:to-primary-800 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 active:scale-[0.98]"
              >
                Explorar Negocios
              </button>
            </div>
          </div>
        </div>
      )
    }
    
    if (!store) return null // Fallback de seguridad
    
    // Calcular próxima hora de apertura si el negocio está cerrado
    const nextOpening = !store.isActive ? getNextOpeningTime(store.operatingHours) : null
    
    // Calcular tiempo hasta la próxima apertura
    const calculateTimeUntilOpening = (nextOpening: { day: string; time: string } | null): string | null => {
      if (!nextOpening) return null
      
      const now = new Date()
      const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
      const dayIndex = dayNames.indexOf(nextOpening.day)
      
      if (dayIndex === -1) return null
      
      const [hours, minutes] = nextOpening.time.split(':').map(Number)
      const nextOpeningDate = new Date()
      const currentDay = now.getDay()
      
      // Calcular días hasta la apertura
      let daysUntilOpening = 0
      if (dayIndex > currentDay) {
        daysUntilOpening = dayIndex - currentDay
      } else if (dayIndex < currentDay) {
        daysUntilOpening = 7 - currentDay + dayIndex
      } else {
        // Es hoy, verificar si ya pasó la hora
        const currentTime = now.getHours() * 60 + now.getMinutes()
        const openingTime = hours * 60 + minutes
        if (currentTime >= openingTime) {
          // Ya pasó la hora de hoy, será la próxima semana
          daysUntilOpening = 7
        }
      }
      
      nextOpeningDate.setDate(now.getDate() + daysUntilOpening)
      nextOpeningDate.setHours(hours, minutes, 0, 0)
      
      const diffMs = nextOpeningDate.getTime() - now.getTime()
      if (diffMs < 0) return null
      
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
      
      if (diffHours < 1) {
        return `en ${diffMinutes} minuto${diffMinutes !== 1 ? 's' : ''}`
      }
      
      if (diffHours < 24) {
        return `en ${diffHours} hora${diffHours !== 1 ? 's' : ''}${diffMinutes > 0 ? ` y ${diffMinutes} minuto${diffMinutes !== 1 ? 's' : ''}` : ''}`
      }
      
      const diffDays = Math.floor(diffHours / 24)
      const remainingHours = diffHours % 24
      if (remainingHours === 0) {
        return `en ${diffDays} día${diffDays !== 1 ? 's' : ''}`
      }
      return `en ${diffDays} día${diffDays !== 1 ? 's' : ''} y ${remainingHours} hora${remainingHours !== 1 ? 's' : ''}`
    }
    
    const timeUntilOpening = calculateTimeUntilOpening(nextOpening)
    
    const storeSubtotal = items.reduce((sum, item) => sum + (Number(item.total) || 0), 0)
    const deliveryFee = store.deliveryFee || 0
    const minOrderAmount = store.minOrderAmount || 0
    // Calcular ITBIS solo si el negocio cobra ITBIS
    const chargesItbis = store.chargesItbis !== false // Por defecto true si no está definido
    const taxAmount = chargesItbis ? Math.round(storeSubtotal * 0.18) : 0 // 18% ITBIS
    const storeTotal = storeSubtotal + deliveryFee + taxAmount
    const meetsMinimum = storeSubtotal >= minOrderAmount

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pb-20 sm:pb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          {/* Header */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-4 sm:mb-6 sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 sm:gap-4">
                <button
                  onClick={() => navigate(`/stores/${storeId}/catalog`)}
                  className="p-2 sm:p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-xl sm:rounded-2xl transition-all duration-200 active:scale-95"
                >
                  <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
                </button>
                <div>
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Mi Carrito</h1>
                  <p className="text-xs sm:text-sm text-gray-600 mt-0.5">{store.name}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  if (confirm(`¿Vaciar carrito de ${store.name}?`)) {
                    clearStoreCart(storeId!)
                    navigate(`/stores/${storeId}/catalog`)
                  }
                }}
                className="text-xs sm:text-sm text-red-600 hover:text-red-700 font-medium px-3 py-1.5 hover:bg-red-50 rounded-xl transition-colors active:scale-95"
              >
                Vaciar
              </button>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border border-gray-100 p-8 sm:p-12 text-center">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center">
                <ShoppingCart className="h-12 w-12 text-primary-600" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Tu carrito está vacío</h2>
              <p className="text-gray-600 mb-8 text-sm sm:text-base">Agrega productos de {store.name}</p>
              <button
                onClick={() => navigate(`/stores/${storeId}/catalog`)}
                className="px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-2xl hover:from-primary-700 hover:to-primary-800 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 active:scale-[0.98]"
              >
                Ver Catálogo
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-3 sm:space-y-4">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 overflow-hidden"
                  >
                    <div className="p-4 sm:p-5 lg:p-6">
                      <div className="flex gap-3 sm:gap-4 lg:gap-5">
                        {/* Product Image */}
                        <div className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 flex-shrink-0 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl overflow-hidden shadow-sm">
                          <ProductImage
                            imageKey={item.variant?.image_url || item.product.imageUrl || null}
                            alt={item.product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 mb-1 text-sm sm:text-base lg:text-lg line-clamp-2">
                            {item.product.name}
                            {item.variant && (
                              <span className="text-primary-600 font-normal ml-2">
                                - {item.variant.variant_name}
                              </span>
                            )}
                          </h3>
                          {item.product.description && (
                            <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 line-clamp-2">
                              {item.product.description}
                            </p>
                          )}
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
                            <span className="text-lg sm:text-xl lg:text-2xl font-bold text-primary-600">
                              RD${(Number(item.total) || 0).toFixed(2)}
                            </span>
                            <span className="text-xs sm:text-sm text-gray-500">
                              RD${(Number(item.product.price) || 0).toFixed(2)} / {item.product.unit || 'unidad'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Quantity Controls */}
                      <div className="flex items-center justify-between mt-4 sm:mt-5 pt-4 sm:pt-5 border-t border-gray-100">
                        <div className="flex items-center gap-2 sm:gap-3 bg-gray-50 rounded-xl sm:rounded-2xl p-1">
                          <button
                            onClick={() => handleQuantityChange(storeId!, item.id, item.quantity - 1)}
                            className="p-2 sm:p-2.5 bg-white hover:bg-gray-100 active:bg-gray-200 rounded-lg sm:rounded-xl transition-all duration-200 active:scale-95 shadow-sm"
                          >
                            <Minus className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
                          </button>
                          <span className="w-10 sm:w-12 text-center font-bold text-gray-900 text-sm sm:text-base">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => handleQuantityChange(storeId!, item.id, item.quantity + 1)}
                            disabled={item.quantity >= (item.variant ? (item.variant.stock_quantity || 0) : (item.product.stockQuantity || 999))}
                            className="p-2 sm:p-2.5 bg-white hover:bg-gray-100 active:bg-gray-200 rounded-lg sm:rounded-xl transition-all duration-200 active:scale-95 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                          >
                            <Plus className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
                          </button>
                        </div>
                        <button
                          onClick={() => {
                            if (confirm(`¿Eliminar ${item.product.name} del carrito?`)) {
                              removeItem(storeId!, item.id)
                            }
                          }}
                          className="p-2 sm:p-3 text-red-600 hover:bg-red-50 active:bg-red-100 rounded-xl sm:rounded-2xl transition-all duration-200 active:scale-95"
                        >
                          <Trash2 className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border border-gray-100 p-5 sm:p-6 lg:p-7 sticky top-4">
                  <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-5 sm:mb-6">Resumen del Pedido</h2>

                  <div className="space-y-3 sm:space-y-4 mb-5 sm:mb-6">
                    <div className="flex justify-between text-sm sm:text-base">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-semibold text-gray-900">RD${storeSubtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm sm:text-base">
                      <span className="text-gray-600">Entrega</span>
                      <span className="font-semibold text-gray-900">RD${deliveryFee.toFixed(2)}</span>
                    </div>
                    {chargesItbis && (
                      <div className="flex justify-between text-sm sm:text-base">
                        <span className="text-gray-600">ITBIS (18%)</span>
                        <span className="font-semibold text-gray-900">RD${taxAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t-2 border-gray-200 pt-4 sm:pt-5 flex justify-between">
                      <span className="font-bold text-gray-900 text-base sm:text-lg">Total</span>
                      <span className="font-bold text-primary-600 text-xl sm:text-2xl lg:text-3xl">RD${storeTotal.toFixed(2)}</span>
                    </div>
                  </div>

                  {!store.isActive && (
                    <div className="mb-5 sm:mb-6 p-4 sm:p-5 bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-xl sm:rounded-2xl">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          <Clock className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm sm:text-base text-red-800 font-semibold mb-2">
                            ⚠️ El negocio está cerrado en este momento
                          </p>
                          <p className="text-xs sm:text-sm text-red-700 mb-2">
                            No puedes realizar pedidos en este horario. Puedes ver la información del negocio, pero no podrás completar la compra hasta que esté abierto.
                          </p>
                          {nextOpening && (
                            <div className="mt-3 pt-3 border-t border-red-200">
                              <p className="text-xs sm:text-sm text-red-800 font-medium">
                                <span className="font-semibold">Próxima apertura:</span> {nextOpening.day} a las {nextOpening.time}
                                {timeUntilOpening && (
                                  <span className="block mt-1 text-red-600">
                                    ({timeUntilOpening})
                                  </span>
                                )}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {!meetsMinimum && store.isActive && (
                    <div className="mb-5 sm:mb-6 p-4 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl sm:rounded-2xl">
                      <p className="text-sm sm:text-base text-yellow-800 font-medium">
                        Faltan <span className="font-bold">RD${((Number(minOrderAmount) || 0) - (Number(storeSubtotal) || 0)).toFixed(2)}</span> para el pedido mínimo de <span className="font-bold">RD${(Number(minOrderAmount) || 0).toFixed(2)}</span>
                      </p>
                    </div>
                  )}

                  <button
                    onClick={() => handleCheckout(storeId!)}
                    disabled={!meetsMinimum || !store.isActive}
                    className={`w-full py-4 sm:py-5 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg transition-all duration-300 active:scale-[0.98] ${
                      meetsMinimum && store.isActive
                        ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 shadow-lg hover:shadow-xl'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {!store.isActive ? 'Negocio Cerrado' : meetsMinimum ? 'Finalizar Compra' : 'Pedido Mínimo No Alcanzado'}
                  </button>
                  
                  {!store.isActive && (
                    <button
                      onClick={() => navigate(`/stores/${storeId}`)}
                      className="w-full mt-3 py-3 text-primary-600 hover:text-primary-700 font-semibold text-sm sm:text-base rounded-xl hover:bg-primary-50 transition-colors active:scale-[0.98] border border-primary-200"
                    >
                      Ver Información del Negocio
                    </button>
                  )}

                  <button
                    onClick={() => navigate(`/stores/${storeId}/catalog`)}
                    className="w-full mt-4 py-3 text-primary-600 hover:text-primary-700 font-semibold text-sm sm:text-base rounded-xl hover:bg-primary-50 transition-colors active:scale-[0.98]"
                  >
                    Seguir Comprando
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Si no estamos en ningún negocio, mostrar lista de negocios con productos
  if (isEmpty) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4 pb-20">
        <div className="text-center max-w-md w-full">
          <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-12">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center">
              <ShoppingCart className="h-12 w-12 text-primary-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Tu carrito está vacío</h2>
            <p className="text-gray-600 mb-8 text-sm sm:text-base">Agrega productos de tus negocios favoritos</p>
            <button
              onClick={() => navigate('/stores')}
              className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-2xl hover:from-primary-700 hover:to-primary-800 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 active:scale-[0.98]"
            >
              Explorar Negocios
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Mostrar lista de negocios con productos en el carrito
  const storeIds = Object.keys(storeCarts)

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pb-20 sm:pb-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-4 sm:mb-6 sticky top-0 z-10">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 sm:p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-xl sm:rounded-2xl transition-all duration-200 active:scale-95"
            >
              <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Mi Carrito</h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                Selecciona un negocio para ver tu carrito
              </p>
            </div>
          </div>
        </div>

        {/* Message */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl sm:rounded-3xl p-5 sm:p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <StoreIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-base sm:text-lg mb-1">No estás en ningún negocio</h3>
              <p className="text-sm sm:text-base text-gray-700">
                Selecciona uno de los negocios donde tienes productos en el carrito para ver y completar tu compra.
              </p>
            </div>
          </div>
        </div>

        {/* Store Cards */}
        <div className="space-y-4 sm:space-y-5">
          {storeIds.map((storeId) => {
            const storeCart = storeCarts[storeId]
            const store = storeCart.store
            const items = storeCart.items
            const itemCount = items.reduce((sum, item) => sum + item.quantity, 0)
            const storeTotal = items.reduce((sum, item) => sum + (Number(item.total) || 0), 0)

            return (
              <div
                key={storeId}
                className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer active:scale-[0.98]"
                onClick={() => handleNavigateToStoreCart(storeId)}
              >
                <div className="p-5 sm:p-6 lg:p-7">
                  <div className="flex items-center gap-4 sm:gap-5">
                    {/* Store Logo */}
                    <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl overflow-hidden shadow-sm">
                      <StoreImage imageKey={store.logoUrl || null} type="logo" className="w-full h-full object-cover" />
                    </div>

                    {/* Store Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 text-lg sm:text-xl lg:text-2xl mb-1 truncate">
                        {store.name}
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600 mb-2">
                        {itemCount} {itemCount === 1 ? 'producto' : 'productos'} en el carrito
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-lg sm:text-xl font-bold text-primary-600">
                          RD${storeTotal.toFixed(2)}
                        </span>
                        <span className="text-xs sm:text-sm text-gray-500">total</span>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                        <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Action Buttons */}
        <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button
            onClick={() => navigate('/stores')}
            className="flex-1 px-6 py-4 bg-white border-2 border-gray-200 text-gray-700 rounded-xl sm:rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 active:scale-[0.98]"
          >
            Explorar Negocios
          </button>
          <button
            onClick={() => {
              if (confirm('¿Estás seguro de vaciar todos los carritos?')) {
                clearCart()
              }
            }}
            className="flex-1 px-6 py-4 bg-red-50 border-2 border-red-200 text-red-700 rounded-xl sm:rounded-2xl font-semibold hover:bg-red-100 transition-all duration-300 active:scale-[0.98]"
          >
            Vaciar Todo
          </button>
        </div>
      </div>
    </div>
  )
}

export default Cart
